import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import '../styles/Catalog.css'

// ── WhatsApp config ───────────────────────────────────────────
const WA_NUMBERS = [
  { label: 'Khetan Singh', number: '919840538852' },
  { label: 'Hitesh',       number: '919940334044' },
]

const makeEnquiryURL = (number, item) => {
  const msg = `Hello! I am interested in your *${item.category}* (${item.label}) from your 2026 catalogue. Could you please share more details and pricing?`
  return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`
}

// ── Category map ──────────────────────────────────────────────
const PAGE_CATEGORIES = {
  'Acrylic Awards':    [1,2,3,4,5,6,7,8,9,10,11,12],
  'Crystal Trophies':  [13,14,15,16,17,18,19,20],
  'Metal Trophies':    [21,22,23,24,25,26,27,28,29,30],
  'Corporate Shields': [31,32,33,34,35,36,37,38,39,40],
  'Star Trophies':     [41,42,43,44,45,46,47,48],
  'Wooden Awards':     [49,50,51,52,53,54],
}

const CATEGORY_ICONS = {
  'Acrylic Awards':    '🔮',
  'Crystal Trophies':  '💎',
  'Metal Trophies':    '🏆',
  'Corporate Shields': '🎖️',
  'Star Trophies':     '⭐',
  'Wooden Awards':     '🪵',
}

const ALL_ITEMS = Array.from({ length: 54 }, (_, i) => {
  const page = i + 1
  const padded = String(page).padStart(4, '0')
  const cat = Object.entries(PAGE_CATEGORIES).find(([, pages]) => pages.includes(page))?.[0] || 'General'
  return {
    id: page,
    img: `/images/2026_ProductCatalog_page-${padded}.jpg`,
    page: padded,
    name: `${cat} — Page ${page}`,
    category: cat,
    label: `Page ${page}`,
  }
})

const ALL_CATEGORIES = ['All', ...Object.keys(PAGE_CATEGORIES)]

// ── WhatsApp Number Picker Modal ──────────────────────────────
function WAPickerModal({ item, onClose }) {
  const ref = useRef(null)

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="wa-modal-overlay">
      <div className="wa-modal" ref={ref}>
        <button className="wa-modal-close" onClick={onClose}>✕</button>

        <div className="wa-modal-header">
          <div className="wa-modal-icon">
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
              <path d="M16 2C8.268 2 2 8.268 2 16c0 2.482.65 4.81 1.787 6.83L2 30l7.363-1.763A13.935 13.935 0 0016 30c7.732 0 14-6.268 14-14S23.732 2 16 2z" fill="#25D366"/>
              <path d="M22.555 19.615c-.315-.157-1.862-.918-2.15-1.022-.287-.105-.496-.157-.705.157-.21.315-.81 1.022-.993 1.232-.183.21-.367.236-.682.079-.315-.158-1.33-.49-2.532-1.56-.936-.833-1.568-1.861-1.752-2.176-.183-.315-.02-.485.138-.642.14-.14.315-.366.472-.55.158-.183.21-.314.315-.524.105-.21.053-.394-.026-.55-.079-.158-.705-1.7-.966-2.328-.254-.612-.513-.529-.705-.538l-.6-.01c-.21 0-.55.079-.838.394-.288.315-1.1 1.075-1.1 2.62 0 1.547 1.126 3.043 1.283 3.252.158.21 2.216 3.384 5.37 4.745.75.324 1.336.517 1.791.662.752.239 1.437.205 1.978.124.604-.09 1.862-.761 2.124-1.496.263-.735.263-1.365.184-1.496-.079-.13-.288-.21-.603-.367z" fill="#fff"/>
            </svg>
          </div>
          <div>
            <div className="wa-modal-title">Chat on WhatsApp</div>
            <div className="wa-modal-sub">
              Enquiring about: <strong>{item.category} — {item.label}</strong>
            </div>
          </div>
        </div>

        <div className="wa-modal-list">
          {WA_NUMBERS.map((n, i) => (
            <a
              key={i}
              href={makeEnquiryURL(n.number, item)}
              target="_blank"
              rel="noopener noreferrer"
              className="wa-modal-row"
              onClick={onClose}
            >
              <div className="wa-modal-avatar">
                {n.label.charAt(0)}
              </div>
              <div className="wa-modal-info">
                <span className="wa-modal-name">{n.label}</span>
                <span className="wa-modal-num">+{n.number.replace(/^91/, '91 ')}</span>
              </div>
              <span className="wa-modal-arrow">→</span>
            </a>
          ))}
        </div>

        <div className="wa-modal-msg-preview">
          <span className="wa-modal-msg-label">Message preview:</span>
          <span className="wa-modal-msg-text">
            Hello! I am interested in your <strong>{item.category}</strong> ({item.label}) from your 2026 catalogue. Could you please share more details and pricing?
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Lightbox ──────────────────────────────────────────────────
function Lightbox({ item, onClose, onPrev, onNext, onEnquire }) {
  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape')      onClose()
      if (e.key === 'ArrowRight')  onNext()
      if (e.key === 'ArrowLeft')   onPrev()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, onNext, onPrev])

  return (
    <div className="lb-overlay" onClick={onClose}>
      <div className="lb-box" onClick={e => e.stopPropagation()}>
        <button className="lb-close" onClick={onClose} title="Close (Esc)">✕</button>
        <button className="lb-arrow lb-prev" onClick={onPrev} title="Previous">&#8592;</button>
        <button className="lb-arrow lb-next" onClick={onNext} title="Next">&#8594;</button>

        <div className="lb-card">
          <div className="lb-img-wrap">
            <div className="lb-img-bg" style={{ backgroundImage: `url(${item.img})` }} />
            <img src={item.img} alt={item.name} className="lb-img" />
          </div>

          <div className="lb-info">
            <div className="lb-meta">
              <span className="lb-cat-badge">
                {CATEGORY_ICONS[item.category]} {item.category}
              </span>
              <span className="lb-page">Page {item.id} / 54</span>
            </div>
            <h3 className="lb-title">{item.name}</h3>
            <button className="lb-enquire" onClick={() => onEnquire(item)}>
              <span>Enquire About This Product</span>
              <span className="lb-enq-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main Catalog Page ─────────────────────────────────────────
export default function Catalog() {
  const [activeCategory, setActiveCategory] = useState('All')
  const [search, setSearch]                 = useState('')
  const [lightbox, setLightbox]             = useState(null)
  const [view, setView]                     = useState('grid')
  const [loaded, setLoaded]                 = useState({})
  const [waPicker, setWaPicker]             = useState(null) // item to enquire about
  const searchRef = useRef(null)

  const filtered = useMemo(() => {
    let list = ALL_ITEMS
    if (activeCategory !== 'All') list = list.filter(i => i.category === activeCategory)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(i =>
        i.category.toLowerCase().includes(q) ||
        i.label.toLowerCase().includes(q) ||
        i.page.includes(q)
      )
    }
    return list
  }, [activeCategory, search])

  const openLightbox  = idx => setLightbox(idx)
  const closeLightbox = () => setLightbox(null)

  const openEnquiry = (item, e) => {
    if (e) e.stopPropagation()
    setWaPicker(item)
  }
  const closeEnquiry = () => setWaPicker(null)

  // Lock body scroll when lightbox OR wa-picker open
  useEffect(() => {
    if (lightbox !== null || waPicker !== null) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev || '' }
    }
  }, [lightbox, waPicker])

  const prevItem = () => setLightbox(p => (p - 1 + filtered.length) % filtered.length)
  const nextItem = () => setLightbox(p => (p + 1) % filtered.length)
  const markLoaded = id => setLoaded(p => ({ ...p, [id]: true }))

  return (
    <div className="catalog-page">

      {/* ── Hero ── */}
      <div className="catalog-hero">
        <div className="catalog-hero-inner">
          <span className="catalog-hero-tag">2026 Collection</span>
          <h1 className="catalog-hero-title">
            Product <span className="gold-grad">Catalogue</span>
          </h1>
          <p className="catalog-hero-sub">
            Browse our complete range of 54 premium award designs — from gleaming metal
            trophies to elegant wooden accolades.
          </p>

          <div className="catalog-search-wrap">
            <span className="search-ico">🔍</span>
            <input
              ref={searchRef}
              type="text"
              className="catalog-search"
              placeholder="Search by category, page number…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')}>✕</button>
            )}
          </div>

          <div className="catalog-hero-stats">
            <span><strong>54</strong> pages</span>
            <span className="dot">·</span>
            <span><strong>6</strong> categories</span>
            <span className="dot">·</span>
            <span><strong>200+</strong> designs</span>
          </div>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="catalog-filter-bar">
        <div className="catalog-filter-inner">
          <div className="filter-pills">
            {ALL_CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`filter-pill ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat !== 'All' && <span className="pill-ico">{CATEGORY_ICONS[cat]}</span>}
                {cat}
                {cat !== 'All' && (
                  <span className="pill-count">{PAGE_CATEGORIES[cat].length}</span>
                )}
              </button>
            ))}
          </div>

          <div className="filter-right">
            <span className="filter-result-count">
              {filtered.length} of {ALL_ITEMS.length} pages
            </span>
            <div className="view-toggle">
              <button className={`vt-btn ${view === 'grid' ? 'active' : ''}`} onClick={() => setView('grid')} title="Grid view">⊞</button>
              <button className={`vt-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')} title="List view">☰</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Grid / List ── */}
      <div className="catalog-body">
        {filtered.length === 0 ? (
          <div className="catalog-empty">
            <span className="empty-ico">🔍</span>
            <h3>No results found</h3>
            <p>Try a different category or clear your search.</p>
            <button className="btn-primary" onClick={() => { setSearch(''); setActiveCategory('All') }}>
              Reset Filters
            </button>
          </div>
        ) : (
          <div className={`catalog-grid ${view === 'list' ? 'list-view' : ''}`}>
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                className={`cat-card ${loaded[item.id] ? 'cat-card--loaded' : ''}`}
                onClick={() => openLightbox(idx)}
                style={{ animationDelay: `${(idx % 12) * 0.04}s` }}
              >
                <div className="cat-card-img-wrap">
                  {!loaded[item.id] && <div className="cat-skeleton" />}
                  <img
                    src={item.img}
                    alt={item.name}
                    className="cat-card-img"
                    onLoad={() => markLoaded(item.id)}
                    loading="lazy"
                  />
                  <div className="cat-card-overlay">
                    <span className="cat-overlay-zoom">🔍 View</span>
                  </div>
                  <span className="cat-card-badge">{item.category}</span>
                  <span className="cat-page-num">{item.label}</span>
                </div>
                <div className="cat-card-foot">
                  <span className="cat-card-cat">
                    {CATEGORY_ICONS[item.category]} {item.category}
                  </span>
                  <button
                    className="cat-enquire-btn"
                    onClick={e => openEnquiry(item, e)}
                  >
                    Enquire →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Bottom CTA ── */}
      <div className="catalog-bottom-cta">
        <div className="catalog-cta-inner">
          <h2>Can't find what you're looking for?</h2>
          <p>We create fully custom designs. Share your vision and we'll craft the perfect award.</p>
          <div className="catalog-cta-btns">
            <button
              className="btn-cta-primary"
              onClick={() => setWaPicker({ category: 'Custom Design', label: 'Custom Enquiry' })}
            >
              <span className="btn-cta-shine" />
              <span>📞 Talk to an Expert</span>
              <span className="btn-cta-arrow">→</span>
            </button>
            <a
              href="/brochures/SuperLaserIndia_Catalogue_2026.pdf"
              download
              className="btn-cta-secondary"
            >
              ⬇ Download PDF Catalogue
            </a>
          </div>
        </div>
      </div>

      {/* ── Lightbox ── */}
      {lightbox !== null && (
        <Lightbox
          item={filtered[lightbox]}
          onClose={closeLightbox}
          onPrev={prevItem}
          onNext={nextItem}
          onEnquire={item => { closeLightbox(); openEnquiry(item) }}
        />
      )}

      {/* ── WhatsApp Number Picker ── */}
      {waPicker !== null && (
        <WAPickerModal item={waPicker} onClose={closeEnquiry} />
      )}
    </div>
  )
}